import { isReadOnlyDiffStatus } from '~/constants/task-status'
import { useTaskStatus } from '~/hooks'
import { useCodeReviewEditable } from './useCodeReviewEditable'

/**
 * Visibility conditions that determine UI control visibility.
 */
interface VisibilityConditions {
  /** Code review is editable (user permissions, index availability, etc.) */
  isEditable: boolean
  /** Task status is view-only (e.g., DRAFT_GENERATED - download only) */
  isViewOnlyStatus: boolean
}

/**
 * Visibility flags for code editor UI controls.
 */
interface CodeEditorVisibility {
  /** Whether the code review is editable */
  isEditable: boolean
  /** Whether to show the approve button */
  showApproveButton: boolean
  /** Whether to show feedback controls (textarea + button) */
  showFeedbackControls: boolean
}

/**
 * Map of visibility flags to their required conditions.
 * Each flag is true when ALL its conditions are met.
 *
 * | Flag                 | Conditions                        |
 * |----------------------|-----------------------------------|
 * | showApproveButton    | isEditable AND NOT isViewOnlyStatus |
 * | showFeedbackControls | isEditable AND NOT isViewOnlyStatus |
 */
function computeVisibility(conditions: VisibilityConditions): CodeEditorVisibility {
  const { isEditable, isViewOnlyStatus } = conditions

  return {
    isEditable,
    showApproveButton: isEditable && !isViewOnlyStatus,
    showFeedbackControls: isEditable && !isViewOnlyStatus
  }
}

/**
 * Hook to determine visibility of code editor UI controls.
 *
 * Visibility rules:
 * - DRAFT_GENERATED and similar statuses are "view-only":
 *   - User can view diffs and download files
 *   - Approve and Provide Feedback buttons are hidden
 *   - This is intentional: draft outputs are for review, not action
 */
export function useCodeEditorVisibility(): CodeEditorVisibility {
  const isEditable = useCodeReviewEditable()
  const taskStatus = useTaskStatus()

  const conditions: VisibilityConditions = {
    isEditable,
    isViewOnlyStatus: isReadOnlyDiffStatus(taskStatus)
  }

  return computeVisibility(conditions)
}
